import { useQuery } from '@tanstack/react-query';
import { api } from '~/lib/api-client';
import { proBuilderKeys } from '~/lib/query-keys';
import { endsSessionPoll } from '~/hooks/queries/use-pro-builder-session';

/** What a card line is about (`ConsentLineKind` in src/pro-builder/consent.ts); the card draws an icon per kind. */
export type ProBuilderConsentLineKind =
  | 'app'
  | 'agent'
  | 'run'
  | 'tool'
  | 'values'
  | 'data'
  | 'memory'
  | 'ask'
  | 'runAs'
  | 'publish'
  | 'audience'
  | 'notice'
  | 'other';

export interface ProBuilderConsentLine {
  kind: ProBuilderConsentLineKind;
  /** The line in plain words. */
  text: string;
  /** A notice's opening sentence, which `text` starts with; set in bold. */
  lead?: string;
}

/** The card as `GET /api/pro-builder/consent` answers it (`WaitingConsent` in src/pro-builder/consent.ts). */
export interface ProBuilderConsent {
  /** The question batch's id, as a string; the answer goes to `POST /api/pro-builder/consent/:batchId`. */
  batchId: string;
  heading: string;
  lines: ProBuilderConsentLine[];
  /** FRONT-3230: the card shows the "Next time" choice, and its answer may carry one. Absent from an older server. */
  modeChoice?: boolean;
  createdAt: string;
  expiresAt: string;
}

export const PRO_BUILDER_CONSENT_PATH = '/api/pro-builder/consent';
/** The card shows up and goes away within seconds of the terminal asking or the answer landing. */
export const PRO_BUILDER_CONSENT_POLL_MS = 3000;

/**
 * FRONT-3108: the consent card waiting on the person's live terminal, polled
 * every 3 s while `enabled` (the Pro Builder window is open and mounted) and
 * re-read when the window opens. 401/404 (signed out, flag off) end the poll.
 * Disabled returns a stable null: a disabled query can still hold cached rows.
 */
export function useProBuilderConsent(enabled: boolean): ProBuilderConsent | null {
  const { data } = useQuery({
    queryKey: proBuilderKeys.consent(),
    queryFn: () => api.get<{ consent: ProBuilderConsent | null }>(PRO_BUILDER_CONSENT_PATH),
    enabled,
    refetchInterval: (q) => (endsSessionPoll(q.state.error) ? false : PRO_BUILDER_CONSENT_POLL_MS),
    refetchOnMount: 'always',
    retry: (_count, err) => !endsSessionPoll(err),
    select: (response) => response.consent,
  });
  return enabled ? (data ?? null) : null;
}
